import React from "react"
import autobind from "class-autobind"
import { IJobConsolidatedTimecardData, IDate, EDayOfWeek, IPayrollPayCalendar, ISelectedFilterTemplateId, IUserFilter } from "../../../models"
import strings from "../../../localization/strings"
import DateRangeSelect_SelectWeek from "../../../components/common/DateRangeSelect_SelectWeek"
import * as api from "../../../api"
import { CircularProgress } from "material-ui"
import ConsolidatedTimecardTimeSummary from "../../../components/job/consolidated-timecard/ConsolidatedTimecardTimeSummary"
import ConsolidatedTimecardEmployeeTable from "../../../components/job/consolidated-timecard/ConsolidatedTimecardEmployeeTable"
import TimecardPopup from "../../../components/timecard/timecard_popup/TimecardPopup"
import AppStateStore from "../../../stores/AppStateStore"
import * as timeHelper from "../../../helpers/timeHelper"
import moment from "moment"
import * as utils from "../../../helpers/utils"


interface IProps {
    jobId: string
    referenceDate: IDate
    selectedPayrollPayCalendar: IPayrollPayCalendar
    selectedFilterTemplateId: ISelectedFilterTemplateId
    handleChangeReferenceDate(referenceDate: IDate): void
    userFilter: IUserFilter
}
interface IState {
    loading: boolean
    startDate: IDate
    endDate: IDate
    consolidatedTimecardData: IJobConsolidatedTimecardData
    timecardPopupEmployeeId: string

}

export default class JobDetailConsolidatedTimecards extends React.Component<IProps, IState> {
    private reloadPageAferCloseTimecardPopup = false
    private _isMounted: boolean = false

    constructor(props: IProps) {
        super(props)
        const weekRange = timeHelper.getWeekRange(EDayOfWeek.Monday, props.referenceDate)
        const storedStartDate = utils.getCookie("JobDetailConsolidatedTimecardStartDate")
        const storedEndDate = utils.getCookie("JobDetailConsolidatedTimecardEndDate")
        this.state = {
            loading: true,
            startDate: storedStartDate === "null" ? moment(weekRange.startDate).utc(true).format() : storedStartDate,
            endDate: storedEndDate === "null" ? moment(weekRange.endDate).utc(true).format() : storedEndDate,
            consolidatedTimecardData: null,
            timecardPopupEmployeeId: null,
        }
        autobind(this)
    }

    componentDidMount() {
        this._isMounted = true
        // Only load timecards if we have filter data
        if (this.props.selectedFilterTemplateId !== null) {
            this.loadTimecards()
        }
    }

    componentWillUnmount() {
        this._isMounted = false
        localStorage.setItem("findd_JobDetailConsolidatedTimecardStartDate", null)
        localStorage.setItem("findd_JobDetailConsolidatedTimecardEndDate", null)
    }

    componentDidUpdate(prevProps: IProps) {
        // Load timecards when filters are ready or when they change
        if (prevProps.selectedFilterTemplateId === null && this.props.selectedFilterTemplateId !== null) {
            this.loadTimecards()
        } else if (
            prevProps.selectedFilterTemplateId !== this.props.selectedFilterTemplateId ||
            prevProps.userFilter !== this.props.userFilter
        ) {
            this.loadTimecards()
        }
    }

    loadTimecards(cb: Function = null) {
        if (!this._isMounted) return;

        this.setState({ loading: true }, () => {
            api.getJobConsolidatedTimecardDataV2(
                this.props.jobId,
                moment(this.state.startDate).parseZone().utc(true).format(),
                moment(this.state.endDate).parseZone().endOf("day").utc(true).format(),
                this.props.userFilter
            ).then(res => {
                if (this._isMounted) {
                    this.setState({
                        loading: false,
                        consolidatedTimecardData: res.data
                    })
                    cb && typeof cb === "function" && cb()
                }
            }).catch(() => {
                if (this._isMounted) {
                    this.setState({ loading: false })
                }
            })
        })
    }

    closeTimcardPopup() {
        this.setState({ timecardPopupEmployeeId: null })
        if (this.reloadPageAferCloseTimecardPopup) {
            AppStateStore.setProgressSnackbarContent("loading")
            this.loadTimecards(() => AppStateStore.setProgressSnackbarContent(null))
            this.reloadPageAferCloseTimecardPopup = false
        }
    }

    render() {
        return (
            <div className="detail-tab" style={{ paddingTop: 10 }}>
                <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
                    <DateRangeSelect_SelectWeek
                        startDate={this.state.startDate}
                        endDate={this.state.endDate}
                        sameDatesAllowed
                        selectedPayrollPayCalendar={this.props.selectedPayrollPayCalendar}
                        handleChange={(startDate, endDate, isValid) =>
                            isValid &&
                            this.setState({ startDate, endDate, loading: true }, () => {
                                localStorage.setItem("findd_JobDetailConsolidatedTimecardStartDate", startDate as string)
                                localStorage.setItem("findd_JobDetailConsolidatedTimecardEndDate", endDate as string)
                                this.loadTimecards()
                                this.props.handleChangeReferenceDate(startDate)
                            })
                        }
                    />
                </div>
                <div style={{ clear: "both" }}>
                    {this.state.loading ?
                        <div className="spinner"><CircularProgress /></div> :
                        !this.state.consolidatedTimecardData.Timecards.length ?
                            <h2 style={{ textAlign: "center", marginTop: 25 }}>
                                {strings.No_timecard_found}
                            </h2> :
                            <div>
                                <ConsolidatedTimecardTimeSummary
                                    consolidatedTimecardTimeSummary={this.state.consolidatedTimecardData.TimeTotals}
                                />
                                <ConsolidatedTimecardEmployeeTable
                                    consolidatedTimecards={this.state.consolidatedTimecardData.Timecards}
                                    handleOpenTimecardPopup={employeeId => this.setState({ timecardPopupEmployeeId: employeeId })}
                                />
                            </div>
                    }
                </div>
                {this.state.timecardPopupEmployeeId &&
                    <TimecardPopup
                        open
                        filterByJobId={this.props.jobId}
                        employeeId={this.state.timecardPopupEmployeeId}
                        referenceDate={this.state.startDate}
                        handleClose={this.closeTimcardPopup}
                        handleRecalcPage={() => (this.reloadPageAferCloseTimecardPopup = true)}
                        hideInfoIcon
                    />
                }
            </div>
        )
    }
}
